import axios from 'axios';
import apiClient, { ApiRequestError, getApiErrorMessage } from './apiClient';

const unwrapApiData = (data) => data?.data || data?.Data || data;

const publicShareClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'https://api.risman.pro/api',
  headers: {
    Accept: 'application/json'
  },
  timeout: 60000
});

export const SHARE_ERROR_CODES = {
  invalid: 'invalid',
  revoked: 'revoked',
  deleted: 'deleted',
  expired: 'expired'
};

export class SharedConversationError extends Error {
  constructor(code, message, statusCode) {
    super(message);
    this.name = 'SharedConversationError';
    this.code = code;
    this.statusCode = statusCode;
  }
}

export const buildShareUrl = (token) => {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/share/${encodeURIComponent(token)}`;
  }
  return `https://risman.pro/share/${encodeURIComponent(token)}`;
};

export const normalizeShare = (item) => {
  const token = item?.Token || item?.token || '';
  return {
    shareId: item?.ShareId || item?.shareId || null,
    token,
    shareUrl: item?.ShareUrl || item?.shareUrl || (token ? buildShareUrl(token) : ''),
    createdAt: item?.CreatedAt || item?.createdAt || null
  };
};

export const normalizeShareStatus = (item) => {
  const shared = Boolean(item?.Shared ?? item?.shared);
  if (!shared) {
    return { shared: false, shareId: null, token: '', shareUrl: '', createdAt: null };
  }
  return { shared: true, ...normalizeShare(item) };
};

export const normalizeSharedConversation = (item) => {
  const messages = item?.Messages || item?.messages || [];
  return {
    title: item?.Title || item?.title || '',
    createdAt: item?.CreatedAt || item?.createdAt || null,
    messages: messages.map((message, index) => {
      const text = message?.Message || message?.message || '';
      return {
        id: message?.Id || message?.id || `shared-${index}`,
        role: `${message?.Role || message?.role || 'assistant'}`.toLowerCase(),
        message: text,
        content: text,
        imageUrls: message?.ImageUrls || message?.imageUrls || [],
        generatedImageUrls: message?.GeneratedImageUrls || message?.generatedImageUrls || [],
        quotedText: message?.QuotedText || message?.quotedText || null,
        createdAt: message?.CreatedAt || message?.createdAt || null
      };
    })
  };
};

const mapPublicShareError = (error) => {
  const status = error?.response?.status || error?.statusCode;
  const message = getApiErrorMessage(error, '');
  const lowered = `${message}`.toLowerCase();

  if (status === 410 && lowered.includes('expired')) {
    return new SharedConversationError(SHARE_ERROR_CODES.expired, message, status);
  }
  if (status === 410) {
    return new SharedConversationError(SHARE_ERROR_CODES.revoked, message, status);
  }
  if (status === 404 && lowered.includes('no longer available')) {
    return new SharedConversationError(SHARE_ERROR_CODES.deleted, message, status);
  }
  return new SharedConversationError(SHARE_ERROR_CODES.invalid, message || 'This shared conversation doesn\'t exist.', status || 404);
};

export const fetchConversationShareStatusApi = async (conversationId) => {
  try {
    const { data } = await apiClient.get(`/conversations/${encodeURIComponent(conversationId)}/share`);
    return normalizeShareStatus(unwrapApiData(data));
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to load share status.'));
  }
};

export const createConversationShareApi = async (conversationId) => {
  try {
    const { data } = await apiClient.post(`/conversations/${encodeURIComponent(conversationId)}/share`);
    return normalizeShare(unwrapApiData(data));
  } catch (error) {
    throw new ApiRequestError(getApiErrorMessage(error, "Couldn't create share link. Please try again."), error?.response?.status);
  }
};

export const revokeConversationShareApi = async (conversationId, shareId) => {
  try {
    await apiClient.delete(
      `/conversations/${encodeURIComponent(conversationId)}/share/${encodeURIComponent(shareId)}`
    );
  } catch (error) {
    throw new Error(getApiErrorMessage(error, 'Failed to disable share link.'));
  }
};

export const fetchSharedConversationApi = async (token) => {
  try {
    const { data } = await publicShareClient.get(`/share/${encodeURIComponent(token)}`);
    return normalizeSharedConversation(unwrapApiData(data));
  } catch (error) {
    throw mapPublicShareError(error);
  }
};
